import { useState } from 'react'
import { X, MessageSquare, Bug, Lightbulb, HelpCircle, Send, CheckCircle } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'

const TYPES = [
  { value: 'bug', label: 'Bug', icon: Bug },
  { value: 'sugestao', label: 'Sugestão', icon: Lightbulb },
  { value: 'outro', label: 'Outro', icon: HelpCircle },
] as const

type FeedbackType = typeof TYPES[number]['value']

interface FeedbackModalProps {
  onClose: () => void
}

export function FeedbackModal({ onClose }: FeedbackModalProps) {
  const { user } = useAuthStore()
  const [type, setType] = useState<FeedbackType>('sugestao')
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')

  const handleSubmit = async () => {
    if (!message.trim()) return
    setStatus('sending')
    try {
      const res = await fetch('https://formsubmit.co/ajax/regenerai@gaasbrasil.com.br', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          _subject: `Feedback Beta - RegenerAI App [${type}]`,
          _cc: 'vladimir@digitalsolutions4y.com,syntropy@gaasbrasil.com.br',
          _template: 'box',
          _captcha: 'false',
          nome: user?.name || 'Usuário',
          email: user?.email || '',
          tipo: type,
          mensagem: message.trim(),
        }),
      })
      if (!res.ok) throw new Error()
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />

      <div
        className="relative bg-brand-surface border border-brand-border rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 animate-fade-in-up"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-brand-surface-alt text-brand-text-muted transition-colors"
        >
          <X size={18} />
        </button>

        {status === 'success' ? (
          <div className="flex flex-col items-center py-6 gap-3 text-center">
            <div className="w-12 h-12 rounded-2xl bg-brand-primary-light flex items-center justify-center">
              <CheckCircle size={24} className="text-brand-primary" />
            </div>
            <h3 className="text-base font-semibold text-brand-text">Feedback enviado!</h3>
            <p className="text-sm text-brand-text-secondary">
              Obrigado pela sua contribuição. Isso nos ajuda a melhorar o RegenerAI.
            </p>
            <button
              onClick={onClose}
              className="mt-2 px-5 py-2 bg-brand-primary text-white rounded-xl text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Fechar
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-brand-primary-light flex items-center justify-center flex-shrink-0">
                <MessageSquare size={20} className="text-brand-primary" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-brand-text">Enviar Feedback</h3>
                <p className="text-xs text-brand-text-muted">Sua opinião nos ajuda a melhorar</p>
              </div>
            </div>

            {/* Type selector */}
            <div className="flex gap-2 mb-4">
              {TYPES.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => setType(value)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium border transition-all duration-200 ${
                    type === value
                      ? 'bg-brand-primary-light border-brand-primary text-brand-primary'
                      : 'border-brand-border text-brand-text-secondary hover:bg-brand-surface-alt'
                  }`}
                >
                  <Icon size={14} />
                  {label}
                </button>
              ))}
            </div>

            {/* Message */}
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Descreva seu feedback..."
              rows={4}
              className="w-full resize-none text-sm rounded-xl px-4 py-3 bg-white dark:bg-verde-800 text-verde-900 dark:text-verde-50 border border-brand-border placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
            />

            {status === 'error' && (
              <p className="text-xs text-red-500 mt-2">
                Erro ao enviar. Tente novamente.
              </p>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium rounded-xl border border-brand-border text-brand-text-secondary hover:bg-brand-surface-alt transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={!message.trim() || status === 'sending'}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl bg-brand-primary text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={14} />
                {status === 'sending' ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
